import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "./AuthContext";
import { consumePulseNext } from "./pulse/PulseGate";
import { RefreshCw, CheckCircle, XCircle, Send, MailCheck, Smartphone, ArrowRight } from "lucide-react";
import { BACKEND_URL } from "./lib/backend";

const API = `${BACKEND_URL}/api`;

// Custom URL scheme registered by the native iOS app. Tapping this from a
// browser will hand the URL (and the magic-link token) to the app via
// `.onOpenURL` (SwiftUI) / `application(_:open:options:)` (UIKit), which then
// POSTs the token to /api/auth/verify and stores the resulting JWT.
const APP_URL_SCHEME = "tourismintel://auth/verify";

function isIOSDevice() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  return /iPhone|iPod|iPad/.test(ua);
}

export default function VerifyPage({ darkMode }) {
  const [searchParams] = useSearchParams();
  const { login } = useAuth();
  const navigate = useNavigate();
  const dm = darkMode;

  // ── Top-level state ──────────────────────────────────────────────────────
  // "ios_choose"  → iPhone visitor; show "Open in app" CTA, DON'T consume token yet
  // "verifying"   → about to POST the token to /api/auth/verify
  // "success"     → token consumed, web user logged in, redirecting to /feed
  // "error"       → token rejected (used / expired / bad)
  const token = searchParams.get("token");
  const [status, setStatus] = useState(() => {
    if (!token) return "error_no_token";
    return isIOSDevice() ? "ios_choose" : "verifying";
  });
  const [errorDetail, setErrorDetail] = useState({
    heading: "Link problem",
    body: "This magic link is no longer valid.",
    canResend: true,
  });

  // Resend-link inline form state
  const [resendEmail, setResendEmail] = useState("");
  const [resendStatus, setResendStatus] = useState("idle"); // idle | sending | sent | error
  const [resendMsg, setResendMsg] = useState("");

  // Guard against React 18 Strict Mode double-invocation.
  const verifyStarted = useRef(false);
  const schemeAttempted = useRef(false);

  // ── Token consumption (web fallback or non-iOS auto-flow) ────────────────
  const consumeToken = () => {
    if (verifyStarted.current) return;
    verifyStarted.current = true;
    setStatus("verifying");

    // POST so email security scanners can't pre-consume the token.
    axios.post(`${API}/auth/verify`, { token })
      .then((res) => {
        login(res.data.access_token, res.data.user, res.data.csrf_token);
        setStatus("success");
        // Pulse deep-link resume: if an anonymous visitor hit a gated /pulse
        // route, the path was stored (one-shot) — land there instead of /feed.
        const pulseNext = consumePulseNext();
        setTimeout(() => navigate(pulseNext || "/feed"), 1500);
      })
      .catch((err) => {
        const detail = err.response?.data?.detail || "";
        if (detail.includes("already been used") || detail.includes("request a new one")) {
          setErrorDetail({
            heading: "Link already used",
            body: "This magic link was already consumed (possibly by an email security scanner or by the TourismIntel app on another device). Enter your email below to get a fresh one.",
            canResend: true,
          });
        } else if (detail.includes("expired")) {
          setErrorDetail({
            heading: "Link expired",
            body: "This link has expired — login links last 30 minutes, registration links 24 hours. Get a fresh link below.",
            canResend: true,
          });
        } else if (detail.includes("suspended") || detail.includes("newsletter") || detail.includes("resubscribe")) {
          setErrorDetail({
            heading: "Subscription inactive",
            body: "Your newsletter subscription is inactive. Access to TourismIntel requires an active subscription. Go to the homepage to resubscribe.",
            canResend: false,
          });
        } else if (detail.includes("not found") || detail.toLowerCase().includes("invalid")) {
          setErrorDetail({
            heading: "Link not found",
            body: "This link is invalid or was replaced by a newer email. Check your inbox for a more recent email, or request a new link below.",
            canResend: true,
          });
        } else {
          setErrorDetail({ heading: "Verification failed", body: detail || "Something went wrong. Please try again.", canResend: true });
        }
        setStatus("error");
      });
  };

  // ── Initial routing: non-iOS visitors get the legacy auto-consume flow.
  // iOS visitors land on the "Open in app" chooser.
  useEffect(() => {
    if (status === "verifying" && !verifyStarted.current) {
      consumeToken();
    }
    // No deps — runs once on mount.
  }, []);

  // ── Open in app (custom URL scheme) ──────────────────────────────────────
  // Strategy: build `tourismintel://auth/verify?token=xxx` and assign it to
  // window.location. If the app is installed, iOS hands the URL to the app
  // and the browser tab loses focus. If the app is NOT installed, the
  // navigation silently fails (modern iOS no longer shows the "Cannot open"
  // dialog for unknown schemes) and the user stays on this page.
  //
  // We DO NOT preserve the token in the web-session: the token is single-use
  // and we want the APP to consume it, not the web tab. So we just leave the
  // page state at "ios_choose" — if the user taps the fallback link, only
  // then we POST it.
  const openInApp = () => {
    if (!token || schemeAttempted.current) return;
    schemeAttempted.current = true;
    const target = `${APP_URL_SCHEME}?token=${encodeURIComponent(token)}`;
    // Use an invisible iframe to avoid a visible browser navigation flash —
    // works around the Chrome iOS scheme-handling quirk.
    try {
      window.location.href = target;
    } catch {
      // Some browsers throw if the scheme is unknown; ignore silently.
    }
  };

  // Auto-trigger the scheme once on mount for iOS visitors. If the app is
  // installed it opens immediately; otherwise the user can tap the visible
  // CTA below. We add a tiny delay so React has time to render the page first.
  useEffect(() => {
    if (status === "ios_choose") {
      const t = setTimeout(openInApp, 300);
      return () => clearTimeout(t);
    }
  }, [status]);

  // ── Resend handler (unchanged) ───────────────────────────────────────────
  const handleResend = async (e) => {
    e.preventDefault();
    if (!resendEmail.trim()) return;
    setResendStatus("sending");
    setResendMsg("");
    try {
      const res = await axios.post(`${API}/auth/register`, {
        email: resendEmail.trim().toLowerCase(),
        privacy_consented: true,
        consent_source: "verify_resend",
      });
      setResendStatus("sent");
      setResendMsg(
        res.data.email_sent
          ? "Magic link sent — check your inbox."
          : "Account confirmed but email could not be delivered. Please contact support."
      );
    } catch (err) {
      const detail = err.response?.data?.detail || "";
      if (err.response?.status === 403 && detail === "newsletter_inactive") {
        setResendStatus("error");
        setResendMsg("Your subscription is inactive. Go to the homepage to resubscribe.");
      } else {
        setResendStatus("error");
        setResendMsg(detail || "Failed to send. Please try again.");
      }
    }
  };

  // ── Render: iOS chooser screen ───────────────────────────────────────────
  if (status === "ios_choose") {
    return (
      <div className={`min-h-screen flex flex-col items-center justify-center px-4 ${dm ? "bg-[#0c1011]" : "bg-[#f8f9fa]"}`}>
        <div
          className={`w-full max-w-md rounded-2xl p-8 ${dm ? "bg-[#1a2122]" : "bg-white"}`}
          style={{ boxShadow: "0 0 0 1px rgba(186,201,204,0.15), 0 8px 40px rgba(25,28,29,0.08)" }}
          data-testid="ios-app-chooser"
        >
          {/* App icon-style header */}
          <div
            className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-5"
            style={{ background: "linear-gradient(135deg, #006874 0%, #0a8a99 100%)" }}
          >
            <Smartphone className="w-7 h-7 text-white" />
          </div>

          <h2 className={`editorial-heading text-2xl font-medium mb-2 text-center ${dm ? "text-slate-100" : "text-[#191c1d]"}`}>
            Open TourismIntel
          </h2>
          <p className={`text-sm text-center mb-7 ${dm ? "text-slate-400" : "text-[#6b7a7d]"}`}>
            We&apos;re signing you in to the app automatically. If it doesn&apos;t open, tap the button below.
          </p>

          {/* Primary CTA: re-trigger the custom URL scheme on tap */}
          <button
            onClick={openInApp.bind(null)}
            data-testid="ios-open-app-btn"
            className="w-full py-3.5 rounded-full text-sm font-semibold tracking-wide flex items-center justify-center gap-2 text-white transition-all hover:opacity-90"
            style={{ background: "#006874" }}
          >
            Open in the TourismIntel app
            <ArrowRight className="w-4 h-4" />
          </button>

          {/* Don't have the app? Get it */}
          <a
            href="https://apps.apple.com/it/app/tourismintel/id6778354345"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="ios-appstore-link"
            className={`mt-3 block w-full py-2.5 rounded-full text-xs font-medium tracking-wide text-center transition-colors ${
              dm ? "bg-white/5 text-slate-300 hover:bg-white/10" : "bg-[#f3f4f5] text-[#3b494c] hover:bg-[#e8ecee]"
            }`}
          >
            Non hai ancora l&apos;app? Scaricala
          </a>

          {/* Divider */}
          <div className={`flex items-center gap-3 my-5 ${dm ? "text-slate-600" : "text-[#bac9cc]"}`}>
            <div className={`flex-1 h-px ${dm ? "bg-white/10" : "bg-black/10"}`} />
            <span className="text-[10px] uppercase tracking-widest">oppure</span>
            <div className={`flex-1 h-px ${dm ? "bg-white/10" : "bg-black/10"}`} />
          </div>

          {/* Web fallback: explicitly consume the token in the browser */}
          <button
            onClick={() => {
              schemeAttempted.current = true; // prevent any further scheme retry
              consumeToken();
            }}
            data-testid="ios-continue-web-btn"
            className={`w-full py-2.5 rounded-full text-xs font-medium tracking-wide underline-offset-2 transition-colors ${
              dm ? "text-slate-400 hover:text-cyan-300" : "text-[#6b7a7d] hover:text-[#006874]"
            }`}
          >
            Continue on the web
          </button>

          <p className={`mt-6 text-[11px] text-center leading-relaxed ${dm ? "text-slate-600" : "text-[#bac9cc]"}`}>
            The magic link works only once:<br />
            use it in the app OR on the web, not both.
          </p>
        </div>
      </div>
    );
  }

  // ── Render: classic flow (verifying / success / error) ───────────────────
  const iconBg =
    status === "verifying" ? (dm ? "bg-[#252e2f]" : "bg-[#f3f4f5]") :
    status === "success"   ? (dm ? "bg-emerald-400/10" : "bg-emerald-50") :
    resendStatus === "sent" ? (dm ? "bg-emerald-400/10" : "bg-emerald-50") :
                              (dm ? "bg-red-400/10" : "bg-red-50");

  const icon =
    status === "verifying" ? <RefreshCw className={`w-7 h-7 animate-spin ${dm ? "text-cyan-400" : "text-[#006874]"}`} /> :
    status === "success"   ? <CheckCircle className="w-7 h-7 text-emerald-500" /> :
    resendStatus === "sent" ? <MailCheck className="w-7 h-7 text-emerald-500" /> :
                              <XCircle className="w-7 h-7 text-red-500" />;

  // Handle the "no token at all" branch by mapping to the same error UI.
  if (status === "error_no_token") {
    if (errorDetail.heading !== "Missing token") {
      // One-time set; React won't loop because we check before setting.
      setErrorDetail({
        heading: "Missing token",
        body: "No token was found in this link. Enter your email below to get a fresh one.",
        canResend: true,
      });
      setStatus("error");
    }
  }

  return (
    <div className={`min-h-screen flex flex-col items-center justify-center px-4 ${dm ? "bg-[#0c1011]" : "bg-[#f8f9fa]"}`}>
      <div
        className={`w-full max-w-md rounded-2xl p-10 ${dm ? "bg-[#1a2122]" : "bg-white"}`}
        style={{ boxShadow: "0 0 0 1px rgba(186,201,204,0.15), 0 8px 40px rgba(25,28,29,0.08)" }}
      >
        {/* Icon */}
        <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-5 ${iconBg}`}>
          {icon}
        </div>

        {/* Heading */}
        <h2 className={`editorial-heading text-2xl font-medium mb-2 text-center ${dm ? "text-slate-100" : "text-[#191c1d]"}`}>
          {status === "verifying" && "Verifying…"}
          {status === "success"   && "Verified. Welcome."}
          {status === "error" && resendStatus === "sent" ? "Check your inbox" : status === "error" ? errorDetail.heading : null}
        </h2>

        {/* Body text */}
        <p className={`text-sm text-center mb-6 ${dm ? "text-slate-400" : "text-[#6b7a7d]"}`}>
          {status === "verifying" && "Checking your magic link…"}
          {status === "success"   && "Redirecting to your feed…"}
          {status === "error" && resendStatus === "sent"
            ? resendMsg
            : status === "error"
            ? errorDetail.body
            : null}
        </p>

        {/* ── Error actions ─────────────────────────────────────────────── */}
        {status === "error" && resendStatus !== "sent" && (
          <>
            {/* Resend form — for all errors where a new link makes sense */}
            {errorDetail.canResend && (
              <form onSubmit={handleResend} className="space-y-3" data-testid="resend-form">
                <div className={`rounded-xl p-1 flex gap-2 ${dm ? "bg-[#252e2f]" : "bg-[#f3f4f5]"}`}>
                  <input
                    type="email"
                    required
                    value={resendEmail}
                    onChange={e => { setResendEmail(e.target.value); if (resendStatus === "error") setResendStatus("idle"); }}
                    placeholder="your@email.com"
                    data-testid="resend-email-input"
                    className={`flex-1 bg-transparent text-sm px-3 py-2 outline-none placeholder:text-sm ${dm ? "text-slate-200 placeholder:text-slate-600" : "text-[#191c1d] placeholder:text-[#bac9cc]"}`}
                  />
                  <button
                    type="submit"
                    disabled={resendStatus === "sending"}
                    data-testid="resend-submit-btn"
                    className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold tracking-wide uppercase transition-all disabled:opacity-60
                      ${dm ? "bg-cyan-400 text-[#001f24] hover:bg-cyan-300" : "bg-[#006874] text-white hover:bg-[#004f58]"}`}
                  >
                    {resendStatus === "sending"
                      ? <><RefreshCw className="w-3.5 h-3.5 animate-spin" /> Sending…</>
                      : <><Send className="w-3.5 h-3.5" /> Send link</>}
                  </button>
                </div>

                {/* Inline resend error */}
                {resendStatus === "error" && (
                  <p className="text-xs text-red-500 text-center" data-testid="resend-error-msg">{resendMsg}</p>
                )}
              </form>
            )}

            {/* Subscription inactive: go to homepage for reactivation */}
            {!errorDetail.canResend && (
              <button
                onClick={() => navigate("/")}
                data-testid="go-homepage-btn"
                className={`w-full py-2.5 rounded-full category-tag transition-all text-center ${dm ? "bg-cyan-400 text-[#001f24] hover:bg-cyan-300" : "bg-[#006874] text-white hover:bg-[#004f58]"}`}
              >
                Go to homepage
              </button>
            )}

            {/* Secondary: back to homepage */}
            <p className={`mt-5 text-xs text-center ${dm ? "text-slate-600" : "text-[#bac9cc]"}`}>
              <button
                onClick={() => navigate("/")}
                data-testid="back-homepage-link"
                className={`underline underline-offset-2 transition-colors ${dm ? "hover:text-slate-400" : "hover:text-[#6b7a7d]"}`}
              >
                Back to homepage
              </button>
            </p>
          </>
        )}

        {/* Post-resend: option to go home */}
        {status === "error" && resendStatus === "sent" && (
          <p className={`text-xs text-center ${dm ? "text-slate-600" : "text-[#bac9cc]"}`}>
            <button
              onClick={() => navigate("/")}
              className={`underline underline-offset-2 transition-colors ${dm ? "hover:text-slate-400" : "hover:text-[#6b7a7d]"}`}
            >
              Back to homepage
            </button>
          </p>
        )}
      </div>
    </div>
  );
}
